
import Vue from "vue";
import { MusicTypes, data, T, GameplayDecisionEvent } from "../App.vue";
import RepeatingBackground from "../components/RepeatingBackground.vue";
import SeaBackground from "../components/SeaBackground.vue";
import BGItem from "../components/BGItem.vue";
import SeaWall from "../components/SeaWall.vue";
import DynamicImage from "../components/DynamicImage.vue";
import PlayerAvatarContainer from "../components/PlayerAvatarContainer.vue";
import PlayerAvatarCounter from "../components/PlayerAvatarCounter.vue";
import GameModal from "../components/GameModal.vue";
import AudioClip from "../components/AudioClip.vue";

enum choices {
  None = -1,
  DoNothing = 0,
  RaiseHouseFloor = 1,
  LayerSeaWall1 = 2,
  LayerSeaWall2 = 3,
  MoveHouseBack = 4,
  SellHouse = 5,
}

enum states {
  Choice = 0,
  SellHouse = 1,
  ChoiceResult = 2,
  SurgeDice_Start = 3,
  SurgeDice_Rolling = 4,
  SurgeDice_Finished = 5,
  Surge = 6,
  RoundFinished = 7,
  Repair = 8,
  AreYouSure = 9,
  GameOver = -1,
}

const maxSeaLevel = 14;

class GameDisplayDataClass {
  Key = 0;
  Round = 1;
  choiceKey = 0;
  game_over_text = "";
  states = states;
  showEmissionsPopUp = false;
  showSafeZonePopUp = false;
  showExitPopup = false;
  private_state = states.Choice;
  isSellingHouse = false;
  bankBalance = 500;
  selectedChoice = choices.None;
  selectedHouse = 0;
  logicalSeaLevel = 0;
  currentHouseIndex = 0;
  surgeLevel = 4;
  instructionKey = "Generic_Pita_Speech_Bubble";
  houses: HouseData[] = [];

  rollTime = 0;
  rollStartTime = 0;

  constructor() {
    //Buildable houses
    this.houses.push(new HouseData(878, 5, 668.21, 711.81, 2000, 2, 2)); //Default
    this.houses.push(new HouseData(661.5, 9, 489, 529.81, 1000, 1, 2)); //Small at risk
    this.houses.push(new HouseData(1042.5, 12, 340, 385.81, 2000, 0, 1)); //Large at risk

    //Game over houses
    this.houses.push(new HouseData(1059.5, 99, 224, 0, 4000, 0, 0)); //Safe luxury
    this.houses.push(new HouseData(659.5, 99, 227, 0, 3000, 0, 0)); //Safe family
    this.houses.push(new HouseData(849.09, 99, 168, 0, 500, 0, 0)); //Safe tiny
    this.houses.push(new HouseData(1205.02, 99, 135, 0, 1000, 0, 0)); //Safe farm
  }
}

class HouseData {
  houseLevel: number;
  houseLevelPx: number;
  houseMiddlePx: number;
  stiltsLevelPx: number;
  price: number;
  maxWallLevel: number;
  moveBackDistance: number;

  hasBeenRaised = false;
  hasBeenMoved = false;
  wallLevel = 0;

  constructor(
    houseMiddlePx: number,
    houseLevel: number,
    houseLevelPx: number,
    stiltsLevelPx: number,
    price: number,
    maxWallLevel: number,
    moveBackDistance: number
  ) {
    this.houseMiddlePx = houseMiddlePx;
    this.houseLevel = houseLevel;
    this.houseLevelPx = houseLevelPx;
    this.stiltsLevelPx = stiltsLevelPx;
    this.price = price;
    this.maxWallLevel = maxWallLevel;
    this.moveBackDistance = moveBackDistance;
  }
}

const zoneSize = 45;
let GameDisplayData = new GameDisplayDataClass();

export default Vue.extend({
  name: "GameDisplay",
  props: {
    tutorialState: Number,
    tutorialOverrideSeaLevel: Number,
  },
  components: {
    AudioClip,
    RepeatingBackground,
    SeaBackground,
    BGItem,
    SeaWall,
    DynamicImage,
    PlayerAvatarContainer,
    PlayerAvatarCounter,
    GameModal,
  },

  created() {
    GameDisplayData = new GameDisplayDataClass();
    window.addEventListener("resize", this.OnResize);
  },

  destroyed() {
    window.removeEventListener("resize", this.OnResize);
  },

  data() {
    return GameDisplayData;
  },

  computed: {
    darkStormBackground: {
      cache: false,
      get(): string {
        if (this.surgeLevel > 2) {
          return "url(" + process.env.BASE_URL + "fast_blured.gif)";
        }
        return "url(" + process.env.BASE_URL + "middle_blured.gif)";
      },
    },

    SVGLeft: {
      cache: false,
      get(): number {
        let adjustedHeight = window.innerHeight / 1024;
        let adjustedWidth = window.innerWidth / adjustedHeight;
        return Math.max(0, (adjustedWidth - 1366) / 2);
      },
    },

    isStorm: {
      get(): boolean {
        return this.state == states.Surge && this.surgeLevel > 1;
      },
    },

    SeaLevelRise: {
      get(): number {
        return data.SeaLevelRise;
      },
      set(value: number): void {
        data.SeaLevelRise = value;
      },
    },

    TotalSurgeLevel: {
      get(): number {
        return Math.min(this.surgeLevel + this.seaLevel, maxSeaLevel);
      },
    },

    VisualHouseLevel: {
      get(): number {
        return this.house.houseLevel - this.house.wallLevel * 2;
      },
    },

    IsSurgeDamaged:{
      get():boolean{
        return this.TotalSurgeLevel >= this.VisualHouseLevel;
      }
    },

    //Get the visual sea level (also affects storms and surge levels)
    seaLevel: {
      get(): number {

        //If the sea hasn't reached the zone before the house, we don't need to take the wall into account
        if (
          this.logicalSeaLevel < this.VisualHouseLevel - 1 ||
          //OR, if the sea has crossed the wall
          this.logicalSeaLevel >= this.house.houseLevel
        ) {
          return this.logicalSeaLevel;
        }

        //If the sea hasn't crossed the wall, but is overlapping the house, set the sea to the zone before the house
        return this.VisualHouseLevel - 1;
      },
    },

    SelectedAvatar: {
      get(): number {
        return data.SelectedAvatar;
      },
    },
    SelectedAvatarName: {
      get(): string {
        return T("Avatar_" + this.SelectedAvatar);
      },
    },
    storm_surge_header: {
      get(): string {
        if (this.state == this.states.SurgeDice_Start) {
          return T("roll_storm_surge_header_default");
        } else if (this.state == this.states.SurgeDice_Finished) {
          return T("roll_storm_surge_header_result_" + this.surgeLevel);
        }
        return "";
      },
    },

    storm_surge_text: {
      get(): string {
        if (this.state == this.states.SurgeDice_Start) {
          return T("roll_storm_surge_text_default");
        } else if (this.state == this.states.SurgeDice_Finished) {
          return T("roll_storm_surge_text_result_" + this.surgeLevel);
        }
        return "";
      },
    },

    diceId: {
      get(): string {
        return "dice-" + this.surgeLevel;
      },
    },

    surgeId: {
      get(): string {
        switch (this.surgeLevel) {
          case 2:
            return "sign_highsurge_growing";
          case 4:
            return "sign_storm_growing";
        }
        return "sign_lowsurge_growing";
      },
    },

    surgeTransitionId: {
      get(): string {
        switch (this.surgeLevel) {
          case 2:
            return "sign_highsurge_pulsating_infinite";
          case 4:
            return "sign_storm_pulsating_infinite";
        }
        return "sign_lowsurge_pulsating_infinite";
      },
    },

    emissionsLabel: {
      get(): string {
        if (this.SeaLevelRise > 1) {
          return T("label_emissions_high");
        }
        return T("label_emissions_low");
      },
    },
    clickCount: {
      cache: false,
      get(): number {
        return data.clickCount;
      },
      set(value: number): void {
        data.clickCount = value;
      },
    },
    state: {
      cache: false,
      get(): states {
        return this.private_state;
      },
      set(value: states) {
        this.private_state = value;
        this.MusicType = MusicTypes.Ambient;
        this.choiceKey++;
      },
    },
    MusicType: {
      get(): MusicTypes {
        return data.MusicType;
      },
      set(value: MusicTypes) {
        if (data.MusicType != value) {
          data.MusicType = value;
          data.MusicKey++;
        }
      },
    },
    counterHouse: {
      get(): number {
        if (this.state == states.SellHouse && this.selectedChoice > -1) {
          return this.selectedChoice + 1;
        }
        return this.currentHouseIndex;
      },
    },
    house: {
      get(): HouseData {
        return this.houses[this.currentHouseIndex];
      },
    },
    seaLevelPx: {
      get(): number {
        if (this.tutorialOverrideSeaLevel > 0) {
          return zoneSize * this.tutorialOverrideSeaLevel;
        }
        return zoneSize * this.seaLevel;
      },
    },
    surgeLevelPx: {
      get(): number {
        return zoneSize * this.TotalSurgeLevel;
      },
    },
    houseValue: {
      get(): number {
        let base = this.house.price;
        if (Math.min(this.seaLevel + 1, maxSeaLevel) >= this.house.houseLevel) {
          return base * 0.0;
        } else if (
          Math.min(this.seaLevel + 2, maxSeaLevel) >= this.house.houseLevel
        ) {
          return base * 0.25;
        } else if (
          Math.min(this.seaLevel + 3, maxSeaLevel) >= this.house.houseLevel
        ) {
          return base * 0.5;
        }
        return base;
      },
    },
    totalWorth: {
      get(): number {
        return this.houseValue + this.bankBalance;
      },
    },
    instruction: {
      get(): string {
        if (this.tutorialState > 0) {
          return T("How_To_Play_" + this.tutorialState);
        }
        return T(this.instructionKey);
      },
    },
  },
  mounted() {
    /*
    window.addEventListener("keydown", (e) => {
      switch (e.key) {
        case "1":
          this.surgeLevel = 1;
          break;
        case "2":
          this.surgeLevel = 2;
          break;
        case "3":
          this.surgeLevel = 4;
          break;
      }
    });
    */
  },
  methods: {
    T(key: string): string {
      return T(key);
    },
    OnResize() {
      this.Key++;
    },
    GameOver(message: string) {
      this.game_over_text = T(message);
      this.state = this.states.GameOver;
    },
    rollStormSurge() {
      this.state = this.states.SurgeDice_Rolling;
      this.rollTime = 0;
      this.rollStartTime = -1;
      requestAnimationFrame(this.rollStormSurge_Update);
    },

    rollStormSurge_Update(time: number) {
      if (this.rollStartTime == -1) {
        this.rollStartTime = time;
      }
      const elapsed = time - this.rollStartTime;

      if (elapsed > 1250) {
        this.state = this.states.SurgeDice_Finished;
        return;
      }
      requestAnimationFrame(this.rollStormSurge_Update);
    },

    toggleEmissions() {
      if (this.SeaLevelRise == 1) {
        this.SeaLevelRise = 2;
      } else {
        this.SeaLevelRise = 1;
      }
    },

    seaLevelMarker(level: number): string {
      if (level == this.house.houseLevel) {
        return "sea-level-marker-zone-red";
      }
      return "";
    },

    canSelectChoice(index: number): boolean {
      switch (this.state) {
        case states.Choice:
          switch (index) {
            case choices.RaiseHouseFloor:
              return (
                this.bankBalance >= 200 && this.house.hasBeenRaised == false
              );
            case choices.LayerSeaWall1:
              return (
                this.bankBalance >= 200 &&
                this.house.wallLevel < this.house.maxWallLevel
              );
            case choices.LayerSeaWall2:
              return (
                this.bankBalance >= 400 &&
                this.house.wallLevel < this.house.maxWallLevel - 1
              );
            case choices.MoveHouseBack:
              return (
                this.bankBalance >= 500 && this.house.hasBeenMoved == false
              );
            default:
              return true;
          }

        case states.SellHouse:
          return (
            this.totalWorth >= this.houses[index + 1].price &&
            index + 1 > this.currentHouseIndex
          );
      }

      return false;
    },

    selectChoice(index: number) {
      data.clickCount++;

      if (index == this.selectedChoice) {
        this.selectedChoice = choices.None;
        return;
      }

      switch (this.state) {
        case states.Choice:
          this.instructionKey = "choice_pita_" + (index + 1);
          if (index + 1 == 5 && this.house.moveBackDistance == 1) {
            this.instructionKey += "_b";
          }
          break;

        case states.SellHouse:
          this.instructionKey = "sell_pita_" + (index + 1);
          break;
      }

      this.selectedChoice = index;
    },

    backClicked() {
      data.clickCount++;
      switch (this.state) {
        case states.AreYouSure:
          this.state = states.SellHouse;
          break;
        case states.SellHouse:
          this.selectedChoice = choices.SellHouse;
          this.isSellingHouse = false;
          this.state = states.Choice;
          break;
        default:
          this.state = states.Choice;
          break;
      }
    },

    nextClicked() {
      switch (this.state) {
        case states.ChoiceResult:
          data.clickCount++;

          this.nextRound();
          return;

        case states.Repair:
          data.clickCount++;
          this.bankBalance -= 500;
          this.finishedRound();
          break;

        case states.RoundFinished:
          data.clickCount++;
          this.bankBalance += 500;

          if (this.SeaLevelRise == 1) {
            this.instructionKey = "pita_nextround_low";
          } else {
            this.instructionKey = "pita_nextround_high";
          }
          this.isSellingHouse = false;
          this.Round += 1;
          this.state = states.Choice;

          break;

        case states.SurgeDice_Finished:
          data.clickCount++;

          if (this.IsSurgeDamaged) {
            this.instructionKey = "make_repairs_pita";
          } else {
            this.instructionKey = "pita_storm_surge_" + this.surgeLevel;
          }

          this.state = states.Surge;
          switch (this.surgeLevel) {
            case 1:
              this.MusicType = MusicTypes.LowStorm;
              break;
            case 2:
              this.MusicType = MusicTypes.HighStorm;
              break;
            case 4:
              this.MusicType = MusicTypes.Cyclone;
              break;
          }
          break;

        case states.AreYouSure:
          data.clickCount++;
          this.sellHouseConfirmed();
          break;

        case states.Surge:
          data.clickCount++;
          if (this.IsSurgeDamaged) {
            this.state = states.Repair;
          } else {
            this.finishedRound();
          }
          break;
      }

      //Sanity check for states that require a valid decision (not all of them do)
      if (!this.canSelectChoice(this.selectedChoice)) {
        return;
      }

      switch (this.state) {
        case states.Choice:
          switch (this.selectedChoice) {
            case choices.DoNothing:
              data.clickCount++;
              GameplayDecisionEvent(
                this.Round,
                this.currentHouseIndex,
                "do_nothing",
                this.SeaLevelRise
              );
              this.nextRound();
              return;

            case choices.RaiseHouseFloor:
              data.powerupCount++;
              GameplayDecisionEvent(
                this.Round,
                this.currentHouseIndex,
                "raise_house_floor",
                this.SeaLevelRise
              );
              this.bankBalance -= 200;
              this.house.houseLevel += 1;
              this.house.hasBeenRaised = true;
              this.house.houseLevelPx -= 35;
              this.showChoiceResult();
              return;

            case choices.LayerSeaWall1:
              data.powerupCount++;
              GameplayDecisionEvent(
                this.Round,
                this.currentHouseIndex,
                "seawall_1_layer",
                this.SeaLevelRise
              );
              this.bankBalance -= 200;
              this.house.wallLevel += 1;
              this.house.houseLevel += 2;
              this.showChoiceResult();
              return;

            case choices.LayerSeaWall2:
              data.powerupCount++;
              GameplayDecisionEvent(
                this.Round,
                this.currentHouseIndex,
                "seawall_2_layer",
                this.SeaLevelRise
              );
              this.bankBalance -= 400;
              this.house.wallLevel += 2;
              this.house.houseLevel += 4;
              this.showChoiceResult();
              return;

            case choices.MoveHouseBack:
              data.powerupCount++;
              GameplayDecisionEvent(
                this.Round,
                this.currentHouseIndex,
                "move_house_back",
                this.SeaLevelRise
              );
              this.bankBalance -= 500;
              this.house.houseLevelPx -= zoneSize * this.house.moveBackDistance;
              this.house.stiltsLevelPx -=
                zoneSize * this.house.moveBackDistance;
              this.house.houseLevel += this.house.moveBackDistance;
              this.house.hasBeenMoved = true;
              this.showChoiceResult();
              return;

            case choices.SellHouse:
              data.popCount++;
              this.selectedChoice = choices.None;
              this.isSellingHouse = true;
              this.state = states.SellHouse;
              return;

            default:
              return;
          }

        case states.SellHouse:
          data.clickCount++;
          this.selectedHouse = this.selectedChoice;
          if (this.currentHouseIndex == 0) {
            this.state = states.AreYouSure;
          } else {
            this.sellHouseConfirmed();
          }

          break;
      }
    },

    showChoiceResult() {
      this.instructionKey = "choice_pita_result_" + (this.selectedChoice + 1);
      if (this.selectedChoice + 1 == 5 && this.house.moveBackDistance == 1) {
        this.instructionKey += "_b";
      }
      this.state = states.ChoiceResult;
    },

    sellHouseConfirmed() {
      let newHouse = "purchased_house_";

      switch (this.selectedHouse) {
        case 0:
          newHouse += "atrisk_small";
          break;
        case 1:
          newHouse += "atrisk_large";
          break;
        case 2:
          newHouse += "safe_luxury_home";
          break;
        case 3:
          newHouse += "safe_family_home";
          break;
        case 4:
          newHouse += "safe_tiny_home";
          break;
        case 5:
          newHouse += "safe_farm_house";
          break;
      }

      GameplayDecisionEvent(
        this.Round,
        this.currentHouseIndex,
        newHouse,
        this.SeaLevelRise
      );

      //Add the cost of the current house (minus sea level deprication) to the bank balance
      this.bankBalance += this.houseValue;

      //Switch to the new house
      this.currentHouseIndex = this.selectedHouse + 1;

      //Subtract the cost of the new house from the total worth (no sea level deprication discount for the new house)
      this.bankBalance -= this.house.price;

      if (this.currentHouseIndex > 2) {
        this.GameOver("gameover_won");
      } else {
        this.nextRound();
      }
    },

    nextRound() {
      const roll = Math.floor(Math.random() * 12);
      if (roll < 2) {
        //Critical!
        this.surgeLevel = 4;
      } else if (roll < 2 + 5) {
        //High!
        this.surgeLevel = 2;
      } else {
        //Low!
        this.surgeLevel = 1;
      }

      this.state = states.SurgeDice_Start;
    },

    finishedRound() {
      this.selectedChoice = -1;
      if (this.Round >= 10) {
        this.GameOver("gameover_10rounds");
      } else {
        this.logicalSeaLevel = Math.min(
          this.logicalSeaLevel + this.SeaLevelRise,
          maxSeaLevel
        );
        if (this.seaLevel >= this.house.houseLevel) {
          this.GameOver("gameover_lost");
        } else {
          this.state = states.RoundFinished;
        }
      }
    },

    choices(): string[] {
      const result = [];

      if (this.isSellingHouse === false) {
        for (let i = 1; i <= 6; i++) {
          result.push(T("choice_" + i));
        }
      } else {
        for (let i = 1; i <= 6; i++) {
          result.push(T("sell_" + i));
        }
      }
      return result;
    },
  },
});
